import { BrowserRouter, Navigate, Routes, Route, useParams } from 'react-router-dom';
import { AuthProvider } from '@edusense/shared-auth-core';
import Layout from './components/Layout';
import AuthGuard from './components/AuthGuard';
import DashboardPage from './pages/DashboardPage';
import ReportsPage from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import CouponPage from './pages/CouponPage';
import AccountsPage from './pages/AccountsPage';
import SupportAdminPage from './pages/SupportAdminPage';
import SiteVisitsPage from './pages/SiteVisitsPage';
import MetricsPage from './pages/MetricsPage';
import BenefitCampaignPage from './pages/BenefitCampaignPage';
import LandingPage from './pages/LandingPage';
import OAuth2CallbackPage from './pages/OAuth2CallbackPage';
import AdminMfaPage from './pages/AdminMfaPage';
import BookAdminPage from './pages/BookAdminPage';
import ReviewAdminPage from './pages/ReviewAdminPage';
import DrawAdminPage from './pages/DrawAdminPage';
import AnnouncementPage from './pages/AnnouncementPage';
import InternalTestersPage from './pages/InternalTestersPage';
import UserReportsPage from './pages/UserReportsPage';
import QLanguageOperationsPage from './pages/QLanguageOperationsPage';
import ContentManagementPage from './pages/ContentManagementPage';
import UgotProAdminPage from './pages/UgotProAdminPage';
import DaonUsageCreditsPage from './pages/DaonUsageCreditsPage';
import { adminAuthConfig } from './auth/adminAuthConfig';
import { ADMIN_HOME_PATH } from './navigation/adminNavigation';
import { AdminApplicationScopeProvider } from './apps/AdminApplicationScope';
import {
  buildAdminApplicationPath,
  getAdminApplication,
  isAdminApplicationId,
} from './apps/adminApplications';

// 공통 레이아웃 + 보안 가드 래퍼
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard>
    <AdminApplicationScopeProvider>
      <Layout>{children}</Layout>
    </AdminApplicationScopeProvider>
  </AuthGuard>
);

const PendingMfaLayout = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard allowPendingMfaAccess>
    {children}
  </AuthGuard>
);

const ApplicationScopedLayout = ({ children }: { children: React.ReactNode }) => {
  const { applicationId } = useParams();
  if (!isAdminApplicationId(applicationId)) {
    return <Navigate to={ADMIN_HOME_PATH} replace />;
  }
  return <ProtectedLayout>{children}</ProtectedLayout>;
};

const ApplicationInternalTestersRoute = () => {
  const { applicationId } = useParams();
  if (!isAdminApplicationId(applicationId)) {
    return <Navigate to={ADMIN_HOME_PATH} replace />;
  }
  const surface = getAdminApplication(applicationId).capabilities.internalTesters;
  if (!surface) {
    return (
      <Navigate
        to={buildAdminApplicationPath(applicationId)}
        replace
      />
    );
  }
  const page = surface === 'sajug-fortune-engine'
    ? <InternalTestersPage />
    : <QLanguageOperationsPage />;
  return <ApplicationScopedLayout>{page}</ApplicationScopedLayout>;
};

const ApplicationContentRoute = () => {
  const { applicationId } = useParams();
  if (!isAdminApplicationId(applicationId)) {
    return <Navigate to={ADMIN_HOME_PATH} replace />;
  }
  if (!getAdminApplication(applicationId).capabilities.content) {
    return <Navigate to={buildAdminApplicationPath(applicationId)} replace />;
  }
  return <Navigate to="/content" replace />;
};

const ApplicationUgotProRoute = () => {
  const { applicationId } = useParams();
  if (!isAdminApplicationId(applicationId)) {
    return <Navigate to={ADMIN_HOME_PATH} replace />;
  }
  if (!getAdminApplication(applicationId).capabilities.ugotPro) {
    return <Navigate to={buildAdminApplicationPath(applicationId)} replace />;
  }
  return <ApplicationScopedLayout><UgotProAdminPage /></ApplicationScopedLayout>;
};

const ApplicationCouponsRoute = () => {
  const { applicationId } = useParams();
  if (!isAdminApplicationId(applicationId)) {
    return <Navigate to={ADMIN_HOME_PATH} replace />;
  }
  if (!getAdminApplication(applicationId).capabilities.coupons) {
    return <Navigate to={buildAdminApplicationPath(applicationId)} replace />;
  }
  return <ApplicationScopedLayout><CouponPage /></ApplicationScopedLayout>;
};

const ApplicationUsageCreditsRoute = () => {
  const { applicationId } = useParams();
  if (applicationId !== 'saju-ai') {
    return <Navigate to={isAdminApplicationId(applicationId) ? buildAdminApplicationPath(applicationId) : ADMIN_HOME_PATH} replace />;
  }
  return <ApplicationScopedLayout><DaonUsageCreditsPage /></ApplicationScopedLayout>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider config={adminAuthConfig}>
        <Routes>
          {/* Public Routes */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/auth/callback" element={<OAuth2CallbackPage />} />
          <Route path="/mfa" element={<PendingMfaLayout><AdminMfaPage /></PendingMfaLayout>} />
          
          {/* Protected Routes */}
          <Route path="/" element={<Navigate to={ADMIN_HOME_PATH} replace />} />
          <Route path="/dashboard" element={<ProtectedLayout><DashboardPage /></ProtectedLayout>} />
          <Route path="/reports" element={<ProtectedLayout><ReportsPage /></ProtectedLayout>} />
          <Route path="/reports/:id" element={<ProtectedLayout><ReportDetailPage /></ProtectedLayout>} />
          <Route path="/apps/:applicationId/accounts" element={<ApplicationScopedLayout><AccountsPage /></ApplicationScopedLayout>} />
          <Route path="/apps/:applicationId/coupons" element={<ApplicationCouponsRoute />} />
          <Route path="/apps/:applicationId/metrics" element={<ApplicationScopedLayout><MetricsPage /></ApplicationScopedLayout>} />
          <Route path="/apps/:applicationId/usage-credits" element={<ApplicationUsageCreditsRoute />} />
          <Route path="/apps/:applicationId/internal-testers" element={<ApplicationInternalTestersRoute />} />
          <Route path="/apps/:applicationId/content" element={<ApplicationContentRoute />} />
          <Route path="/apps/:applicationId/ugot-pro" element={<ApplicationUgotProRoute />} />
          <Route path="/ugot-pro" element={<Navigate to={buildAdminApplicationPath('browser-agent', 'ugot-pro')} replace />} />
          <Route path="/content" element={<ProtectedLayout><ContentManagementPage /></ProtectedLayout>} />
          <Route path="/apps/q-language/operations" element={<Navigate to={buildAdminApplicationPath('q-language', 'internal-testers')} replace />} />
          <Route path="/accounts" element={<Navigate to={buildAdminApplicationPath('sajug', 'accounts')} replace />} />
          <Route path="/coupons" element={<Navigate to={buildAdminApplicationPath('sajug', 'coupons')} replace />} />
          <Route path="/metrics" element={<Navigate to={buildAdminApplicationPath('sajug', 'metrics')} replace />} />
          <Route path="/q-language" element={<Navigate to={buildAdminApplicationPath('q-language', 'internal-testers')} replace />} />
          <Route path="/benefit-campaign" element={<ProtectedLayout><BenefitCampaignPage /></ProtectedLayout>} />
          <Route path="/support" element={<ProtectedLayout><SupportAdminPage /></ProtectedLayout>} />
          <Route path="/site-visits" element={<ProtectedLayout><SiteVisitsPage /></ProtectedLayout>} />
          <Route path="/books" element={<ProtectedLayout><BookAdminPage /></ProtectedLayout>} />
          <Route path="/user-reports" element={<ProtectedLayout><UserReportsPage /></ProtectedLayout>} />
          <Route path="/reviews" element={<ProtectedLayout><ReviewAdminPage /></ProtectedLayout>} />
          <Route path="/reviews/draw" element={<ProtectedLayout><DrawAdminPage /></ProtectedLayout>} />
          <Route path="/announcements" element={<ProtectedLayout><AnnouncementPage /></ProtectedLayout>} />
          <Route path="/internal-testers" element={<Navigate to={buildAdminApplicationPath('sajug', 'internal-testers')} replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
